/**
 * Énumération des pays selon la norme ISO 3166-1 alpha-2
 *
 * Organisée pour un usage européen prioritaire :
 * 1. Pays de l'Union Européenne (27 pays)
 * 2. Autres pays européens
 * 3. Reste du monde par continent
 *
 * Conforme aux standards ISO 3166-1 alpha-2 à jour (2025)
 * Noms officiels en français selon les recommandations de l'ONU
 *
 * @see https://www.iso.org/iso-3166-country-codes.html
 * @see https://commission.europa.eu/resources-partners/the-eus-relations-with-other-countries_fr
 */
export enum Country {
  // =========================================
  // UNION EUROPÉENNE (27 pays) - Ordre alphabétique français
  // =========================================
  DE = 'Allemagne',
  AT = 'Autriche',
  BE = 'Belgique',
  BG = 'Bulgarie',
  CY = 'Chypre',
  HR = 'Croatie',
  DK = 'Danemark',
  ES = 'Espagne',
  EE = 'Estonie',
  FI = 'Finlande',
  FR = 'France',
  GR = 'Grèce',
  HU = 'Hongrie',
  IE = 'Irlande',
  IT = 'Italie',
  LV = 'Lettonie',
  LT = 'Lituanie',
  LU = 'Luxembourg',
  MT = 'Malte',
  NL = 'Pays-Bas',
  PL = 'Pologne',
  PT = 'Portugal',
  CZ = 'Tchéquie',
  RO = 'Roumanie',
  SK = 'Slovaquie',
  SI = 'Slovénie',
  SE = 'Suède',

  // =========================================
  // AUTRES PAYS EUROPÉENS
  // =========================================
  AL = 'Albanie',
  AD = 'Andorre',
  AM = 'Arménie',
  AZ = 'Azerbaïdjan',
  BY = 'Bélarus',
  BA = 'Bosnie-Herzégovine',
  GE = 'Géorgie',
  IS = 'Islande',
  XK = 'Kosovo',
  LI = 'Liechtenstein',
  MK = 'Macédoine du Nord',
  MD = 'Moldavie',
  MC = 'Monaco',
  ME = 'Monténégro',
  NO = 'Norvège',
  GB = 'Royaume-Uni',
  RU = 'Russie',
  SM = 'Saint-Marin',
  RS = 'Serbie',
  CH = 'Suisse',
  TR = 'Turquie',
  UA = 'Ukraine',
  VA = 'Vatican',

  // =========================================
  // TERRITOIRES EUROPÉENS D'OUTRE-MER
  // =========================================
  AX = 'Åland',
  FO = 'Îles Féroé',
  GG = 'Guernesey',
  JE = 'Jersey',
  IM = 'Île de Man',
  GI = 'Gibraltar',
  SJ = 'Svalbard et Jan Mayen',

  // =========================================
  // AFRIQUE
  // =========================================
  ZA = 'Afrique du Sud',
  DZ = 'Algérie',
  AO = 'Angola',
  BJ = 'Bénin',
  BW = 'Botswana',
  BF = 'Burkina Faso',
  BI = 'Burundi',
  CM = 'Cameroun',
  CV = 'Cap-Vert',
  CF = 'République centrafricaine',
  KM = 'Comores',
  CG = 'Congo-Brazzaville',
  CD = 'Congo (Rép. dém.)',
  CI = "Côte d'Ivoire",
  DJ = 'Djibouti',
  EG = 'Égypte',
  ER = 'Érythrée',
  SZ = 'Eswatini',
  ET = 'Éthiopie',
  GA = 'Gabon',
  GM = 'Gambie',
  GH = 'Ghana',
  GN = 'Guinée',
  GW = 'Guinée-Bissau',
  GQ = 'Guinée équatoriale',
  KE = 'Kenya',
  LS = 'Lesotho',
  LR = 'Libéria',
  LY = 'Libye',
  MG = 'Madagascar',
  MW = 'Malawi',
  ML = 'Mali',
  MA = 'Maroc',
  MU = 'Maurice',
  MR = 'Mauritanie',
  MZ = 'Mozambique',
  NA = 'Namibie',
  NE = 'Niger',
  NG = 'Nigéria',
  UG = 'Ouganda',
  RW = 'Rwanda',
  EH = 'Sahara occidental',
  ST = 'Sao Tomé-et-Principe',
  SN = 'Sénégal',
  SC = 'Seychelles',
  SL = 'Sierra Leone',
  SO = 'Somalie',
  SS = 'Soudan du Sud',
  SD = 'Soudan',
  TZ = 'Tanzanie',
  TD = 'Tchad',
  TG = 'Togo',
  TN = 'Tunisie',
  ZM = 'Zambie',
  ZW = 'Zimbabwe',

  // =========================================
  // AMÉRIQUE DU NORD
  // =========================================
  CA = 'Canada',
  US = 'États-Unis',
  MX = 'Mexique',

  // =========================================
  // AMÉRIQUE CENTRALE ET CARAÏBES
  // =========================================
  AI = 'Anguilla',
  AG = 'Antigua-et-Barbuda',
  AW = 'Aruba',
  BS = 'Bahamas',
  BB = 'Barbade',
  BZ = 'Belize',
  BM = 'Bermudes',
  CR = 'Costa Rica',
  CU = 'Cuba',
  CW = 'Curaçao',
  DM = 'Dominique',
  DO = 'République dominicaine',
  GD = 'Grenade',
  GL = 'Groenland',
  GT = 'Guatemala',
  HT = 'Haïti',
  HN = 'Honduras',
  JM = 'Jamaïque',
  KY = 'Îles Caïmans',
  MS = 'Montserrat',
  NI = 'Nicaragua',
  PA = 'Panama',
  PR = 'Porto Rico',
  BQ = 'Pays-Bas caribéens',
  KN = 'Saint-Christophe-et-Niévès',
  LC = 'Sainte-Lucie',
  SX = 'Saint-Martin (partie néerlandaise)',
  MF = 'Saint-Martin (partie française)',
  PM = 'Saint-Pierre-et-Miquelon',
  VC = 'Saint-Vincent-et-les-Grenadines',
  SV = 'Salvador',
  TC = 'Îles Turks-et-Caïcos',
  VG = 'Îles Vierges britanniques',
  VI = 'Îles Vierges des États-Unis',

  // =========================================
  // AMÉRIQUE DU SUD
  // =========================================
  AR = 'Argentine',
  BO = 'Bolivie',
  BR = 'Brésil',
  CL = 'Chili',
  CO = 'Colombie',
  EC = 'Équateur',
  FK = 'Îles Malouines',
  GF = 'Guyane française',
  GY = 'Guyana',
  PY = 'Paraguay',
  PE = 'Pérou',
  SR = 'Suriname',
  UY = 'Uruguay',
  VE = 'Venezuela',

  // =========================================
  // ASIE
  // =========================================
  AF = 'Afghanistan',
  SA = 'Arabie saoudite',
  BH = 'Bahreïn',
  BD = 'Bangladesh',
  BT = 'Bhoutan',
  BN = 'Brunei',
  KH = 'Cambodge',
  CN = 'Chine',
  KP = 'Corée du Nord',
  KR = 'Corée du Sud',
  AE = 'Émirats arabes unis',
  IN = 'Inde',
  ID = 'Indonésie',
  IQ = 'Irak',
  IR = 'Iran',
  IL = 'Israël',
  JP = 'Japon',
  JO = 'Jordanie',
  KZ = 'Kazakhstan',
  KG = 'Kirghizistan',
  KW = 'Koweït',
  LA = 'Laos',
  LB = 'Liban',
  MY = 'Malaisie',
  MV = 'Maldives',
  MN = 'Mongolie',
  MM = 'Myanmar',
  NP = 'Népal',
  OM = 'Oman',
  UZ = 'Ouzbékistan',
  PK = 'Pakistan',
  PS = 'Palestine',
  PH = 'Philippines',
  QA = 'Qatar',
  SG = 'Singapour',
  LK = 'Sri Lanka',
  SY = 'Syrie',
  TJ = 'Tadjikistan',
  TW = 'Taïwan',
  TH = 'Thaïlande',
  TL = 'Timor oriental',
  TM = 'Turkménistan',
  VN = 'Viêt Nam',
  YE = 'Yémen',

  // =========================================
  // OCÉANIE
  // =========================================
  AS = 'Samoa américaines',
  AU = 'Australie',
  CK = 'Îles Cook',
  FJ = 'Fidji',
  GU = 'Guam',
  KI = 'Kiribati',
  MH = 'Îles Marshall',
  FM = 'Micronésie',
  NR = 'Nauru',
  NC = 'Nouvelle-Calédonie',
  NZ = 'Nouvelle-Zélande',
  NU = 'Niue',
  NF = 'Île Norfolk',
  MP = 'Îles Mariannes du Nord',
  PW = 'Palaos',
  PG = 'Papouasie-Nouvelle-Guinée',
  PN = 'Îles Pitcairn',
  PF = 'Polynésie française',
  WS = 'Samoa',
  SB = 'Îles Salomon',
  TK = 'Tokelau',
  TO = 'Tonga',
  TV = 'Tuvalu',
  VU = 'Vanuatu',
  WF = 'Wallis-et-Futuna',

  // =========================================
  // TERRITOIRES FRANÇAIS D'OUTRE-MER
  // =========================================
  BL = 'Saint-Barthélemy',
  GP = 'Guadeloupe',
  MQ = 'Martinique',
  YT = 'Mayotte',
  RE = 'Réunion',
  TF = 'Terres australes et antarctiques françaises',

  // =========================================
  // RÉGIONS SPÉCIALES ADMINISTRATIVES CHINOISES
  // =========================================
  HK = 'Hong Kong',
  MO = 'Macao',

  // =========================================
  // TERRITOIRES INHABITÉS / SPÉCIAUX
  // =========================================
  AQ = 'Antarctique',
  BV = 'Île Bouvet',
  IO = "Territoire britannique de l'océan Indien",
  CX = 'Île Christmas',
  CC = 'Îles Cocos',
  GS = 'Géorgie du Sud-et-les Îles Sandwich du Sud',
  HM = 'Îles Heard-et-MacDonald',
  SH = 'Sainte-Hélène, Ascension et Tristan da Cunha',
  UM = 'Îles mineures éloignées des États-Unis',
}
